import { cn } from "@/lib/utils";

interface Props {
  values: number[];
  width?: number;
  height?: number;
  className?: string;
  /** Draw a faint area under the line. */
  fill?: boolean;
}

/**
 * Minimal dependency-free sparkline. Inherits its color from `currentColor`,
 * so wrap it in a `text-primary` (etc.) element to tint. Renders nothing
 * useful below two points (shows a flat baseline instead).
 */
export function Sparkline({ values, width = 140, height = 40, className, fill = true }: Props) {
  const pad = 2;
  const w = width;
  const h = height;
  const n = values.length;

  if (n === 0) {
    return (
      <svg viewBox={`0 0 ${w} ${h}`} className={cn("w-full h-10", className)} preserveAspectRatio="none" />
    );
  }

  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const stepX = n > 1 ? (w - pad * 2) / (n - 1) : 0;
  const y = (v: number) => h - pad - ((v - min) / range) * (h - pad * 2);
  const x = (i: number) => pad + i * stepX;

  const line = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = `${pad},${h - pad} ${line} ${(pad + (n - 1) * stepX).toFixed(1)},${h - pad}`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className={cn("w-full h-10", className)}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {fill && n > 1 ? <polygon points={area} fill="currentColor" opacity={0.12} /> : null}
      <polyline
        points={line}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
